import { useEffect, useRef, useState } from 'react';

/**
 * Detects horizontal overflow on a scrollable element and returns a ref +
 * a style object that applies a soft fade mask on the overflowing edges.
 * - Right fade appears when content can scroll further right.
 * - Left fade appears when the user has scrolled away from the start.
 */
export default function useOverflowFade(deps = [], options = {}) {
  const { leftFade = true, rightFade = true } = options;
  const ref = useRef(null);
  const [fadeRight, setFadeRight] = useState(false);
  const [fadeLeft, setFadeLeft] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const update = () => {
      const canScrollRight = el.scrollWidth - el.clientWidth - el.scrollLeft > 1;
      const canScrollLeft = el.scrollLeft > 1;
      setFadeRight(canScrollRight && rightFade);
      setFadeLeft(canScrollLeft && leftFade);
    };

    update();

    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);

    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, deps);

  const buildMask = () => {
    if (!fadeLeft && !fadeRight) return undefined;
    const left = fadeLeft ? 'transparent 0, black 28px' : 'black 0';
    const right = fadeRight ? 'black calc(100% - 28px), transparent 100%' : 'black 100%';
    const value = `linear-gradient(to right, ${left}, ${right})`;
    return { WebkitMaskImage: value, maskImage: value };
  };

  return { ref, maskStyle: buildMask() };
}
